import type { ReactNode } from 'react'

import { cn } from '@/lib/utils/cn'
import type { ExecutionStatus, Outcome, RiskLevel } from '@/types/domain'

type Tone = 'ok' | 'fail' | 'denied' | 'waiting' | 'miss' | 'neutral' | 'remote'

const TONES: Record<Tone, string> = {
  ok: 'bg-ok-wash text-ok border-ok/20',
  fail: 'bg-fail-wash text-fail border-fail/20',
  denied: 'bg-denied-wash text-denied border-denied/20',
  waiting: 'bg-waiting-wash text-waiting border-waiting/20',
  miss: 'bg-miss-wash text-miss border-miss/20',
  remote: 'bg-remote-wash text-remote border-remote/20',
  neutral: 'bg-raised text-ink-muted border-line-strong/70',
}

/** The dot carries the tone for anyone who cannot rely on the fill alone. */
const DOTS: Record<Tone, string> = {
  ok: 'bg-ok',
  fail: 'bg-fail',
  denied: 'bg-denied',
  waiting: 'bg-waiting',
  miss: 'bg-miss',
  remote: 'bg-remote',
  neutral: 'bg-ink-faint',
}

export function Badge({
  tone = 'neutral',
  children,
  className,
  title,
}: {
  tone?: Tone
  children: ReactNode
  className?: string
  title?: string
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5',
        'text-micro font-semibold leading-4 tracking-wide',
        TONES[tone],
        className,
      )}
    >
      <span aria-hidden="true" className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOTS[tone])} />
      {children}
    </span>
  )
}

/**
 * Execution status.
 *
 * DENIED and REJECTED are deliberately not red. A policy denial means the safety layer did
 * its job, and a rejection means a human said no — neither is a fault, and colouring them
 * like one would send an operator hunting for a bug that does not exist.
 */
const STATUS_TONE: Record<ExecutionStatus, Tone> = {
  SUCCESS: 'ok',
  FAILED: 'fail',
  DENIED: 'denied',
  REJECTED: 'denied',
  CONFIRMATION_PENDING: 'waiting',
  VALIDATION_FAILED: 'miss',
}

const STATUS_LABEL: Record<ExecutionStatus, string> = {
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  DENIED: 'DENIED',
  REJECTED: 'REJECTED',
  CONFIRMATION_PENDING: 'AWAITING',
  VALIDATION_FAILED: 'INVALID',
}

const STATUS_HINT: Record<ExecutionStatus, string> = {
  SUCCESS: 'Executed and returned a result.',
  FAILED: 'Executed but GitHub or the network returned an error.',
  DENIED: 'Refused by the policy engine. GitHub was never contacted.',
  REJECTED: 'A human declined the change. Nothing was modified.',
  CONFIRMATION_PENDING: 'Waiting for a human decision. Nothing has changed yet.',
  VALIDATION_FAILED: 'Arguments were rejected before anything ran.',
}

export function StatusBadge({ status }: { status: ExecutionStatus }) {
  return (
    <Badge tone={STATUS_TONE[status]} title={STATUS_HINT[status]}>
      {STATUS_LABEL[status]}
    </Badge>
  )
}

const RISK_TONE: Record<RiskLevel, Tone> = {
  READ: 'neutral',
  WRITE: 'waiting',
  DESTRUCTIVE: 'fail',
  META: 'remote',
}

const RISK_HINT: Record<RiskLevel, string> = {
  READ: 'Observes state. Runs immediately.',
  WRITE: 'Changes state on GitHub. Runs immediately, and is recorded in Executions.',
  DESTRUCTIVE: 'Irreversible. Refused by the policy engine and disabled by default.',
  META: "Operates on this server's own state, not GitHub's.",
}

export function RiskBadge({ risk }: { risk: RiskLevel }) {
  return (
    <Badge tone={RISK_TONE[risk]} title={RISK_HINT[risk]}>
      {risk}
    </Badge>
  )
}

/**
 * Benchmark outcome.
 *
 * The whole point of the evaluation system is that WRONG_TOOL and TOOL_FAILED are
 * different problems: one is fixed by rewriting a tool description, the other by fixing
 * code or credentials. They get different colours so the distinction survives a glance.
 */
const OUTCOME_TONE: Record<Outcome, Tone> = {
  CORRECT_TOOL: 'ok',
  WRONG_TOOL: 'miss',
  TOOL_FAILED: 'fail',
  NO_TOOL_SELECTED: 'denied',
}

const OUTCOME_LABEL: Record<Outcome, string> = {
  CORRECT_TOOL: 'CORRECT',
  WRONG_TOOL: 'WRONG TOOL',
  TOOL_FAILED: 'TOOL FAILED',
  NO_TOOL_SELECTED: 'NO TOOL',
}

const OUTCOME_HINT: Record<Outcome, string> = {
  CORRECT_TOOL: 'The expected tool was chosen and it executed successfully.',
  WRONG_TOOL: 'A different tool was chosen. Fix the tool descriptions.',
  TOOL_FAILED: 'The right tool was chosen but execution failed. Fix the code or credentials.',
  NO_TOOL_SELECTED: 'The model answered without calling any tool.',
}

export function OutcomeBadge({ outcome }: { outcome: Outcome }) {
  return (
    <Badge tone={OUTCOME_TONE[outcome]} title={OUTCOME_HINT[outcome]}>
      {OUTCOME_LABEL[outcome]}
    </Badge>
  )
}

export { OUTCOME_HINT, OUTCOME_LABEL, OUTCOME_TONE, STATUS_HINT }
